// Wizard Step 1 — Бараа сонгох (screens-buyer-app §4).
//
// Props-only: renders from WizardState, writes via dispatch. Validations
// R-005/R-006 (>=1 item, qty<=available, one active return per orderItemId —
// the last is enforced by the backend on submit). Fully-returned lines disabled.
import {
  ActionIcon,
  Avatar,
  Box,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { CheckCircleIcon, ImageIcon, MinusIcon, PlusIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../../../utils";
import { TNUM } from "../constants";
import type { WizardAction, WizardSelectedItem, WizardState } from "./state";
import { selectedTotal, selectedCount } from "./state";

interface Props {
  order: { orderNumber: string; deliveredAt: string | null };
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

function ItemRow({
  item,
  dispatch,
}: {
  item: WizardSelectedItem;
  dispatch: React.Dispatch<WizardAction>;
}) {
  const { t } = useTranslation("buyerApp");
  const fullyReturned = item.available <= 0 && item.alreadyReturned > 0;
  const partial = item.alreadyReturned > 0 && item.available > 0;
  const disabled = item.available <= 0;

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      data-disabled={disabled ? "true" : undefined}
      styles={{ root: { opacity: disabled ? 0.55 : 1 } }}
    >
      <Group wrap="nowrap" align="flex-start" gap="sm">
        <Checkbox
          checked={item.selected}
          disabled={disabled}
          onChange={(e) => dispatch({ type: "TOGGLE_ITEM", orderItemId: item.orderItemId, selected: e.currentTarget.checked })}
          aria-label={t("stepItem.selectAria", { name: item.productName })}
          mt={4}
        />
        <Avatar src={item.image || null} size={44} radius="md">
          <ImageIcon size={20} />
        </Avatar>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} lineClamp={1}>
            {item.productName || "-"}
          </Text>
          <Text size="sm" c="dimmed">
            SKU: {item.sku || "-"}
            {item.unit ? ` · ${item.unit}` : ""}
          </Text>
          <Text size="sm" c="dimmed" mt={2} style={TNUM}>
            {t("stepItem.unitCount", { price: currencyFormat(item.unitPrice), count: item.orderedQuantity })}
          </Text>

          {fullyReturned ? (
            <Group gap={6} mt={6} wrap="nowrap" align="center">
              <CheckCircleIcon size={14} weight="fill" color="var(--mantine-color-green-6)" />
              <Text size="sm" c="green.7">{t("stepItem.fullyReturned")}</Text>
            </Group>
          ) : partial ? (
            <Text size="sm" c="orange.7" mt={4}>
              {t("stepItem.partialReturned", { returned: item.alreadyReturned, available: item.available })}
            </Text>
          ) : null}

          {item.selected && !disabled ? (
            <Box mt="sm">
              <Text size="sm" fw={500} mb={4}>
                {t("stepItem.returnQty")}
              </Text>
              <Group gap="xs" align="center" wrap="nowrap">
                <ActionIcon
                  variant="default"
                  size="lg"
                  // B73: stops at 1 — deselecting is the checkbox's job.
                  disabled={item.quantity <= 1}
                  onClick={() => dispatch({ type: "SET_ITEM_QTY", orderItemId: item.orderItemId, quantity: item.quantity - 1 })}
                  aria-label={t("stepItem.decrease")}
                >
                  <MinusIcon size={16} />
                </ActionIcon>
                <NumberInput
                  value={item.quantity}
                  onChange={(v) => dispatch({ type: "SET_ITEM_QTY", orderItemId: item.orderItemId, quantity: Number(v) || 1 })}
                  min={1}
                  max={item.available}
                  clampBehavior="strict"
                  hideControls
                  w={64}
                  styles={{ input: { textAlign: "center", ...TNUM } }}
                  aria-label={t("stepItem.qtyAria")}
                />
                <ActionIcon
                  variant="default"
                  size="lg"
                  disabled={item.quantity >= item.available}
                  onClick={() => dispatch({ type: "SET_ITEM_QTY", orderItemId: item.orderItemId, quantity: item.quantity + 1 })}
                  aria-label={t("stepItem.increase")}
                >
                  <PlusIcon size={16} />
                </ActionIcon>
                <Text size="sm" c="dimmed">
                  {t("stepItem.available", { count: item.available })}
                </Text>
              </Group>

              <Textarea
                mt="sm"
                label={t("stepItem.noteLabel")}
                placeholder={t("stepItem.notePlaceholder")}
                value={item.note}
                onChange={(e) => dispatch({ type: "SET_ITEM_NOTE", orderItemId: item.orderItemId, note: e.currentTarget.value })}
                autosize
                minRows={2}
                maxRows={4}
                styles={{ label: { fontSize: "var(--mantine-font-size-xs)" } }}
              />
            </Box>
          ) : null}
        </Box>
      </Group>
    </Paper>
  );
}

export function StepItemSelect({ order, state, dispatch }: Props) {
  const { t } = useTranslation("buyerApp");
  const items = state.itemOrder.map((id) => state.items[id]);
  const selectableIds = items.filter((it) => it.available > 0);
  const allSelected = selectableIds.length > 0 && selectableIds.every((it) => it.selected);
  const total = selectedTotal(state);
  const count = selectedCount(state);

  return (
    <Stack gap="md">
      <Box>
        <Text fw={600} size="lg">
          {t("stepItem.title")}
        </Text>
        <Text size="sm" c="dimmed">
          {order.orderNumber}
          {order.deliveredAt ? ` · ${t("stepItem.deliveredSuffix", { date: order.deliveredAt.slice(0, 10) })}` : ""}
        </Text>
      </Box>

      <Paper withBorder radius="md" p="sm">
        <Checkbox
          checked={allSelected}
          disabled={selectableIds.length === 0}
          onChange={(e) => dispatch({ type: "SELECT_ALL", selected: e.currentTarget.checked })}
          label={<Text fw={500}>{t("stepItem.selectAll")}</Text>}
        />
      </Paper>

      <Stack gap="sm">
        {items.map((item) => (
          <ItemRow key={item.orderItemId} item={item} dispatch={dispatch} />
        ))}
        {items.length === 0 ? (
          <Text ta="center" c="dimmed" py="md">
            {t("stepItem.noItems")}
          </Text>
        ) : null}
      </Stack>

      <Paper withBorder radius="md" p="md" bg="var(--mantine-color-gray-0)">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {t("stepItem.selectedLabel")}
          </Text>
          <Text fw={600} style={TNUM}>
            {t("stepItem.selectedSummary", { amount: currencyFormat(total), count })}
          </Text>
        </Group>
      </Paper>
    </Stack>
  );
}
